import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { CustomCotationFormData } from "@/types/forms/home/customCotationsForm.form";
import CommentIcon from "@/assets/icons/comment.svg?react";
import AttachmentIcon from "@/assets/icons/attachment.svg?react";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ExpeditionDocumentFileDto, GetAllCompanyDto } from "@/types/api/api-types";
import { StyledSelectSingleValue } from "@/components/styled/StyledSelectSingleValue";
import StyledInput from "../styled/StyledInput";
import { CreditCard, Plus } from "lucide-react";
import { useState } from "react";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { UploadFileClientModal } from "../backOffice/expeditionForm/uploadFileModal";
import { Button } from "../ui/button";
import {
  ExpeditionDocumentCard,
  NewFileDocumentCard,
} from "../backOffice/expeditionForm/expeditionDocumentCard";
import {
  FileAction,
  getCreatableFileTypes,
  getVisibleFileTypes,
  sortDocumentsByPriority,
} from "@/constants/fileCrud";

interface InfoValidationProps {
  documents: ExpeditionDocumentFileDto[];
  isLoading: boolean;
  billingEntities: GetAllCompanyDto["billingEntities"];
}

export const InfoValidation = ({ documents, isLoading, billingEntities }: InfoValidationProps) => {
  const { t } = useTranslation("boQuotations");
  const methods = useFormContext<CustomCotationFormData>();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newFiles, setNewFiles] = useState<{ fileId: string; file: File; type: FileTypeEnum }[]>(
    [],
  );

  const newFilesId = methods.watch("newFilesId");
  const removedFilesId = methods.watch("removedFilesId");
  const readableFileTypes = getVisibleFileTypes(FileAction.FO_CREATE_EXPEDITION);
  const creatableFileTypes = getCreatableFileTypes(FileAction.FO_CREATE_EXPEDITION);
  const readableDocuments = sortDocumentsByPriority(
    documents.filter((document) => readableFileTypes.includes(document.type as FileTypeEnum)),
  );

  const handleDeleteFile = async (fileId: string) => {
    const isNewlyAdded = newFilesId?.includes(fileId);
    const isExisting = documents?.some((document) => document.id === fileId);

    if (isNewlyAdded) {
      methods.setValue("newFilesId", newFilesId?.filter((id) => id !== fileId) || []);
      setNewFiles(newFiles.filter((file) => file.fileId !== fileId));
    } else if (isExisting) {
      methods.setValue("removedFilesId", [...new Set([...(removedFilesId || []), fileId])]);
    }
  };

  const handleUploadSuccess = (fileId: string, selectedType: string, file: File) => {
    setNewFiles([...newFiles, { fileId, file, type: selectedType as FileTypeEnum }]);
    methods.setValue("newFilesId", [...(newFilesId || []), fileId]);
  };

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-6">
      <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <CommentIcon />
          <h2 className="text-xm font-semibold text-black">{t("validation.commentTitle")}</h2>
        </div>
        <textarea
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-normal hover:bg-gray-50"
          rows={4}
          cols={50}
          placeholder="..."
          {...methods.register("comment")}
        />
      </div>
      <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <AttachmentIcon />
          <h2 className="text-xm font-semibold text-black">
            <Trans
              t={t}
              i18nKey="validation.attachmentsTitle"
              components={{ hint: <span className="text-sm font-normal text-gray-500" /> }}
            />
          </h2>
        </div>
        <div className="flex max-h-[70vh] w-full flex-col gap-8 md:flex">
          <div className="flex flex-col gap-2">
            {readableDocuments
              ?.filter((file: ExpeditionDocumentFileDto) => !removedFilesId?.includes(file.id))
              .map((file: ExpeditionDocumentFileDto) => (
                <ExpeditionDocumentCard
                  key={file.id}
                  file={file}
                  onDelete={handleDeleteFile}
                  hideDetailsInfo={true}
                />
              ))}
            {newFiles.map((file) => (
              <NewFileDocumentCard
                key={file.fileId}
                file={file}
                onDelete={handleDeleteFile}
                hideDetailsInfo={true}
              />
            ))}
          </div>

          <Button
            variant="outline"
            type="button"
            className="self-start"
            onClick={() => setUploadModalOpen(true)}
          >
            <Plus className="mr-2 size-4" />
            {t("validation.importFile")}
          </Button>

          <UploadFileClientModal
            open={uploadModalOpen}
            onOpenChange={setUploadModalOpen}
            allowedFileTypes={creatableFileTypes}
            uploadSuccessCallback={handleUploadSuccess}
          />
        </div>
      </div>
      <div className="flex w-full max-w-[800px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <CreditCard />
          <h2 className="text-xm font-semibold text-black">{t("validation.billingTitle")}</h2>
        </div>
        <div className="flex w-full flex-col gap-2">
          <StyledSelectSingleValue
            options={
              billingEntities.map((billingEntity) => ({
                value: billingEntity.id,
                label: billingEntity.companyName,
              })) || []
            }
            setValue={(e: any) => methods.setValue("billingEntityId", e || "")}
            title={t("validation.billingEntityLabel")}
            control={methods.control}
            name="billingEntityId"
            className="w-full"
          />
          <StyledInput
            control={methods.control}
            name="reference"
            title={t("validation.referenceLabel")}
            className="w-full"
          />
        </div>
      </div>
      <div className="flex w-full max-w-[800px] flex-col-reverse gap-3 md:flex-row">
        <button
          disabled={isLoading}
          className="text-normal bg-brand700 hover:bg-brand600 flex-1 px-4 py-2 font-semibold text-white disabled:bg-gray-200 disabled:text-gray-500"
        >
          {isLoading ? <StyledSpinner width="20" height="20" /> : t("validation.submit")}
        </button>
      </div>
    </div>
  );
};
